import {FC} from "react";
import {Box, Stack, TextField, Typography} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import type {Dayjs} from "dayjs";
import dayjs from "dayjs";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";

const DATE_INPUT_FORMAT = "MM/DD/YYYY";

const useStyles = makeStyles()((theme) => ({
	section: {
		padding: theme.spacing(2),
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small
	},
	sectionTitle: {
		fontWeight: theme.typography.fontWeightBold
	},
	helperText: {
		color: theme.palette.text.secondary
	}
}));

export interface CourseEnrollmentDueDateSectionProps {
	dueDate: Dayjs | null;
	onDueDateChange: (value: Dayjs | null) => void;
	disabled?: boolean;
	/** When true, renders only the date picker (for Manage Due Dates modal). */
	embedded?: boolean;
}

export const CourseEnrollmentDueDateSection: FC<CourseEnrollmentDueDateSectionProps> = ({
	dueDate,
	onDueDateChange,
	disabled = false,
	embedded = false
}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();

	const picker = (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<DatePicker
				value={dueDate}
				disabled={disabled}
				minDate={dayjs().startOf("day")}
				inputFormat={DATE_INPUT_FORMAT}
				onChange={onDueDateChange}
				renderInput={(params) => (
					<TextField
						{...params}
						fullWidth
						inputProps={{
							...params.inputProps,
							placeholder: DATE_INPUT_FORMAT
						}}
					/>
				)}
			/>
		</LocalizationProvider>
	);

	if (embedded) {
		return picker;
	}

	return (
		<Box className={classes.section}>
			<Stack spacing={2}>
				<Stack spacing={0.5}>
					<Typography variant="body1" className={classes.sectionTitle}>
						{t("bulk_enrollment_due_date_section_title", "Set a due date (Optional)")}
					</Typography>
					<Typography variant="body2" className={classes.helperText}>
						{t("bulk_enrollment_due_date_section_helper", "Leave blank for no due date.")}
					</Typography>
				</Stack>
				{picker}
			</Stack>
		</Box>
	);
};
